import { useTranslation } from "react-i18next";
import "./Login.css";
import logoIcon from "../../base/assets/icons/logo.svg";
import { Modal, Typography, Button, TextField } from "@mui/material";
import { Grid } from '@mui/material';

import ModalButton from "../../base/reusables/button/Button";
import { useEffect, useState } from "react";
import { styled } from "@mui/system";
import { useNavigate, useLocation } from "react-router-dom";
import AppStore from "../../base/stores/AppStore";
import UserStore from "../../base/stores/UserStore";
import { checkLoginWithoutRedirect } from "../../base/services/loginService";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";

// Styled components using MUI v5 "styled"
const ModalWrapper = styled('div')({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
});

const Paper = styled('div')({
  position: 'absolute',
  paddingTop: '77px',
  height: '254px',
  width: '512px',
  backgroundColor: 'rgba(255, 255, 255, 1)',
  borderRadius: '13px 13px 0 0',
  boxShadow: '0 25px 31px 0 rgba(0, 0, 0, 0.2)',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  borderBottomLeftRadius: '50% 40px !important',
  borderBottomRightRadius: '50% 40px !important',
  '@media (max-width: 600px)': {
    width: '80vw',
  },
});

const ModalBackdrop = styled('div')({
  backgroundColor: 'rgba(0, 15, 30, 0.81)',
  '& div:first-child': {
    backgroundColor: 'unset !important',
  },
});

const Title = styled(Typography)({
  textAlign: 'center',
  fontFamily: 'FbReforma',
  fontSize: '25px',
  color: 'rgba(40, 40, 40, 1)',
  letterSpacing: '0px',
  marginBottom: '41px',
  '@media (max-width: 600px)': {
    fontSize: '16px',
  },
});

const ResetPassword: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate(); 

  const [modalOpen, setModalOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [password, setPassword] = useState("");
  const [showPass, setShowPass] = useState<boolean>(false);
  const [token, setToken] = useState<string>("");
  const { search } = useLocation();
  const [isLoggedIn, setIsLoggedin] = useState<boolean>(false);
  const params = new URLSearchParams(search);

  useEffect(() => {
    checkUserLogin();
  }, []);

  const checkUserLogin = async () => {
    try {
      const { token } = await checkLoginWithoutRedirect();
      if (token) {
        setErrorMessage(t("User-logged-in-error"));
        setModalOpen(true);
        setIsLoggedin(true);
        return;
      }
      checkValidToken();
    } catch (error: any) {
      if (error.statusCode > 400) {
        setErrorMessage(t("GeneralError"));
      } else {
        setErrorMessage(error.errorMessages);
      }
      setModalOpen(true);
    }
  };

  const checkValidToken = async () => {
    try {
      const token = params.get("t");
      if (!token) return navigate("/login");

      await UserStore.checkValidToken(token.toString());
      setToken(token.toString());
    } catch (error: any) {
      if (error.statusCode < 400) {
        setErrorMessage(error.errorMessages);
      } else {
        setErrorMessage(t("GeneralError"));
      }
      setModalOpen(true);
    }
  };

  const redirect = () => {
    setModalOpen(false);
    navigate(`${isLoggedIn ? "/" : "/login"}`);
  };

  const handleSubmit = async (e: React.SyntheticEvent) => {
    e.preventDefault();
    try {
      AppStore.incrementLoadingProcedure();
      await UserStore.resetPassword(password, token);
      setErrorMessage(t("Reset-password-success"));
      setModalOpen(true);
      return;
    } catch (error: any) {
      if (error.statusCode < 400) {
        setErrorMessage(error.errorMessages);
      } else {
        setErrorMessage(t("GeneralError"));
      }
      setModalOpen(true);
    } finally {
      AppStore.decrementLoadingProcedure();
    }
  };

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPassword(e.target.value);
  };

  return (
    <>
      {token && (
        <div className={'background'}>
          <div className={'wrapper'}>
            <div className={'resetPassBox'}>
              <img
                tabIndex={0}
                className={'logo'}
                width="152"
                src={logoIcon}
                alt={t("Header-Logo-alt")}
              />
              <h3 className={'header'}>{t("Reset-password-header")}</h3>
              <form onSubmit={handleSubmit}>
                <Grid container>
                  <Grid className={'register__field'} xs={12}>
                    <TextField
                      onChange={handlePasswordChange}
                      value={password}
                      type={showPass ? "text" : "password"}
                      name="password"
                      fullWidth
                      id="password"
                    variant="standard"
                    className="custom-text-field"
                      label={t("Reset-password-new-pass")}
                      required
                      inputProps={{
                        minLength: parseInt(t("Password-min-chars")),
                        maxLength: parseInt(t("Password-max-chars")),
                      }}
                      helperText={`${t("Validation-MinimumLength")} ${t("Password-min-chars")}`}
                    />
                    {showPass ? (
                      <VisibilityIcon
                        className={'showPass'}
                        onClick={() => setShowPass(!showPass)}
                        fontSize="small"
                      />
                    ) : (
                      <VisibilityOffIcon
                        className={'showPass'}
                        onClick={() => setShowPass(!showPass)}
                        fontSize="small"
                      />
                    )}
                  </Grid>
                </Grid>
                <Button
                  className={'resetPassButton'}
                  aria-label={t("Reset-password-send")}
                  type="submit"
                >
                  {t("Reset-password")}
                </Button>
              </form>
            </div>
          </div>
        </div>
      )}
      <Modal
        aria-labelledby="simple-modal-title"
        aria-describedby="simple-modal-description"
        open={modalOpen}
        onClose={redirect}
      >
        <Paper>
          <Title variant="h6" id="modal-title">
            {errorMessage}
          </Title>
          <ModalButton aria-label={errorMessage} onClick={redirect}>
            {t("got-it")}
          </ModalButton>
        </Paper>
      </Modal>
    </>
  );
};

export default ResetPassword;
